import axios from 'axios';
import Ably from "ably/callbacks";
import { useUserStore } from "@/Stores/userStore";
import { useEnergyConfirmationStore } from "@/Stores/energyConfirmationStore";
import { useCreditStore } from "@/Stores/creditStore";
import { useErrorStore } from "@/Stores/errorStore";
import {useSessionsStore} from "@/Stores/sessionsStore";
import i18n from '@/i18nSetup';

function addLocaleHeader(config) {
    if (i18n.global.locale) {
        config.headers['Accept-Language'] = i18n.global.locale.value;
    }
    return config;
}

let ablyInstance;

const axiosInstance = axios.create({
    timeout: 240000, // 4 minutes in milliseconds
});

function addAuthorizationHeader(config) {
    const userStore = useUserStore();
    const accessToken = userStore.getAccessToken();
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
}

function addSessionHeader(config) {
    const sessionsStore = useSessionsStore();
    const localSessionId = sessionsStore.getSessionId()
    if (localSessionId) {
        config.headers['Session-Id'] = localSessionId;
    }
    return config;
}

export async function estimation(url, data, estimate, title, callback) {
    const energyConfirmationStore = useEnergyConfirmationStore();
    const creditStore = useCreditStore();
    const hasUnlimitedCredit = creditStore.unlimited;
    if (hasUnlimitedCredit) {
        estimate = false;
    }

    const postRequest = async (isEstimate = false) => {
        const headers = isEstimate ? { Estimate: 'true' } : {};
        try {
            const response = await axiosInstance.post(url, data, { headers });
            return response;
        } catch (error) {
            return Promise.reject(error);
        }
    };

    if (!estimate) {
        callback?.();
        return postRequest();
    }

    energyConfirmationStore.displayModal = true;

    try {
        const response = await postRequest(true);
        if (response.data.cost === 0) {
            energyConfirmationStore.displayModal = false;
            callback?.();
            return postRequest();
        }
        energyConfirmationStore.cost = response.data.cost;
        energyConfirmationStore.title = title;
    } catch (error) {
        energyConfirmationStore.displayModal = false;
        throw error;
    }

    return await new Promise((resolve, reject) => {
        energyConfirmationStore.setConfirmationCallback(async () => {
            try {
                console.log("Estimation Confirmed")
                callback?.();
                resolve(await postRequest());

            } catch (error) {
                console.log("Estimation error", error)
                reject(error);
            }
        });
    });
}

export async function initializeAbly() {
    if (ablyInstance) {
        // Check if existing instance is connected or connecting
        const state = ablyInstance.connection.state;
        if (state === 'connected' || state === 'connecting') {
            return ablyInstance;
        }
        // If failed/disconnected, reset instance to try again
        ablyInstance = null;
    }

    const userStore = useUserStore();
    const accessToken = userStore.getAccessToken();
    if (!accessToken) {
        console.error('No access token available for Ably connection');
        return null;
    }

    const socketTokenRequest = userStore.getSocketTokenRequest();
    if (!socketTokenRequest || !socketTokenRequest.token) {
        console.error('No socket token available for Ably connection');
        return null;
    }

    try {
        // Use token directly instead of authCallback to avoid the upgrade parameter issue
        ablyInstance = new Ably.Realtime.Promise({
            token: socketTokenRequest.token,
            clientId: socketTokenRequest.clientId,
            authUrl: '/api/users/refresh-sockets',
            authMethod: 'POST',
            authHeaders: {'Authorization': `Bearer ${accessToken}`},
            disconnectedRetryTimeout: 15000,
            suspendedRetryTimeout: 30000,
        });

        // Wait for connection with timeout
        await Promise.race([
            new Promise((resolve, reject) => {
                ablyInstance.connection.once('connected', resolve);
                ablyInstance.connection.once('failed', (error) => {
                    reject(new Error('Ably connection failed: ' + (error?.reason?.message || 'Unknown error')));
                });
            }),
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Ably connection timeout after 10 seconds')), 10000)
            )
        ]);
        // Monitor connection state changes
        ablyInstance.connection.on('connected', () => {
            console.log('🔗 Ably connection established via:', ablyInstance.connection.connectionManager?.transport?.shortName);
        });

        return ablyInstance;
    } catch (error) {
        console.error('Failed to initialize Ably:', error);
        ablyInstance = null;
        throw error;
    }
}

function solveChallenge(challenge) {
    const { words, char } = challenge;
    // Join all words containing the target character with commas
    const matchingWords = words.filter(word => word.includes(char));
    return `${matchingWords.join(',')}​`;
}

async function handleChallengeResponse(error) {
    if (error.response?.status === 428 && error.response?.data?.challenge_required) {
        const challenge = error.response.data.challenge;
        const solution = solveChallenge(challenge);

        const originalRequest = error.config;
        const originalData = typeof originalRequest.data === 'string'
            ? JSON.parse(originalRequest.data)
            : originalRequest.data || {};

        originalRequest.data = {
            ...originalData,
            challenge_solution: solution
        };

        return axiosInstance.request(originalRequest);
    }
    return Promise.reject(error);
}

axiosInstance.interceptors.request.use(addAuthorizationHeader);
axiosInstance.interceptors.request.use(addSessionHeader);
axiosInstance.interceptors.request.use(addLocaleHeader);
axiosInstance.interceptors.response.use((response) => response, async (error) => {
    // Handle challenge response first
    if (error.response?.status === 428 && error.response?.data?.challenge_required) {
        return handleChallengeResponse(error);
    }

    const errorStore = useErrorStore()

    console.log("Displaying Error", error)
    console.log("Displaying Error Message", error.message)
    console.log("Displaying Error Response", error.response)
    if (error.response?.data?.errors) {
        errorStore.setErrors(error.response.data.errors)
        console.log(errorStore.state.errors)
    }
    return Promise.reject(error)
});

export default axiosInstance
